export const matchingResources = {
  en: {
    matching: {
      language: { label: 'Language', english: 'English', spanish: 'Español' },
      animal: { cat: 'cat', dog: 'dog' },
      nav: {
        defaultLogoAlt: '{{brand}} logo',
        shelterLogoAlt: 'Shelter logo',
        shelterWebsite: '{{name}} website',
        visitShelterWebsite: 'Visit shelter website',
        loadingShelterLogo: 'Loading shelter logo',
      },
      species: {
        headingStart: 'Your',
        headingHighlight: 'forever friend',
        headingEnd: 'awaits you',
        intro:
          "We don't just find pets, we curate lifelong companions. Start your journey by choosing your future teammate.",
        dogChoice: 'Find me a Dog',
        catChoice: 'Find me a Cat',
        dogAlt: 'Golden retriever',
        catAlt: 'Orange tabby cat',
        heroAlt: 'Person smiling while holding a puppy outdoors',
        matches: '5,000+ Matches',
        matchesSubtitle: 'Found their soulmates this month',
        shelterNamed:
          'You’re viewing available pets at {{name}}. Matches are ranked within that shelter only.',
        shelterOnly:
          'Matches are ranked within this shelter’s available pets only (not site-wide).',
      },
      quiz: {
        cancel: 'Cancel',
        heading: "Let's find a {{species}}",
        shelterOnly:
          "Results include only this shelter's available pets in our system.",
        loading: 'Loading questions…',
        loadError: 'Could not load questions',
        matchError: 'Could not load matches',
        retry: 'Retry',
        completeAll: 'Complete each question before finding matches.',
        findMatches: 'Find matches →',
        nextStep: 'Next step →',
        back: 'Back',
        step: 'Step {{current}} of {{total}}',
        progressAria: 'Step {{current}} of {{total}}, {{percent}} percent',
        illustrationAlt: 'A dog and a cat sitting together on a sofa',
        importance: {
          not_at_all: 'Not at all',
          not_very: 'Not very',
          very: 'Very',
        },
        emptyShelter:
          'This shelter has no available {{species}} pets with complete match profiles in our system yet. Try the other species, or check back later.',
      },
      results: {
        back: 'Back',
        restart: 'Restart test',
        heading: 'Your pet matches',
        subtitle:
          "Based on your preferences, here are some pets we think you'll love",
        share: 'Share results',
        adoption: 'Start adoption application',
        shareHelp: 'Add matches before sharing.',
        noMatches: 'No matches yet',
        empty:
          "We couldn't rank any {{species}} records from the shelter data. Go back and try again, or pick the other species.",
        emptyShared: 'No pets were included in this shared result set.',
        matchBadge: '{{percent}}% Match',
        photo: '{{name}} photo',
        petPhoto: 'Pet photo',
        noPhotoAvailable: 'No photo available',
        noPhoto: 'No photo',
        viewProfile: 'View profile',
        viewProfileFor: 'View profile for {{name}}',
        favorite: 'Favorite',
        removeFavorite: 'Remove {{name}} from favorites',
      },
      shared: {
        loading: 'Loading match results…',
        notFound: 'Match results not found',
        unavailable: 'Results unavailable',
        expired:
          'The link may have expired or the quiz was run before sharing was enabled.',
        takeQuiz: 'Take the adoption quiz',
        home: 'Home',
        note: 'Shared {{species}} match results from {{date}}. Rankings reflect availability when the quiz was completed.',
      },
      share: {
        title: 'My pet match results',
        message: 'Check out my personal match results',
        topOne: 'Your top match',
        topThree: 'Top 3 matches',
        all: 'All matches',
        close: 'Close share view',
        slides: 'Share slides',
        previous: 'Previous',
        previousSlide: 'Previous slide',
        next: 'Next',
        nextSlide: 'Next slide',
        slideOf: 'Slide {{current}} of {{total}}',
        dialogDescription:
          'Share your quiz match results. Currently viewing: {{title}}. Slide {{current}} of {{total}}. {{instructions}} Press Escape to close. Public results link: {{url}}',
        autoAdvance: 'Slides advance automatically every {{seconds}} seconds.',
        indicators: 'Use the slide indicators to change slides.',
        buttons: 'Use previous and next buttons to change slides.',
        socials: 'Share to socials',
        via: 'Share via {{channel}}',
        disabled:
          'Run Find matches with share enabled to generate a public share link',
        emailDisabled: 'Run Find matches with share enabled to email results',
        topMatch: 'Top match',
        rank: 'Rank {{rank}}',
        statusShared:
          'Choose {{platform}} in the share menu to post your results, or paste from your clipboard if needed.',
        statusFacebookCopied:
          'Your message and link were copied — open Facebook and paste into a post, story, or message.',
        statusCopied:
          '{{platform}} does not support one-click web sharing. Your message and link were copied — open the {{platform}} app and paste in a story, DM, or post.',
        statusCopyFailed:
          'Could not copy automatically. Share this link: {{url}}',
        statusNoLink:
          'Could not copy automatically. Run Find matches again to generate a share link.',
      },
      email: {
        unavailable: 'Share link is not available. Run Find matches again.',
        invalid: 'Enter a valid email address.',
        disabled:
          'Email sending is disabled in this environment. Try again later.',
        failed: 'Could not send email. Please try again.',
        close: 'Close email dialog',
        heading: 'Email your match results',
        intro:
          "We'll send a summary with your ranked matches to the address below.",
        email: 'Email',
        name: 'First name',
        optional: '(optional)',
        consent: 'By adding your email you agree to share your data',
        missingId:
          'Persisted share ID missing — complete a new match to enable email.',
        cancel: 'Cancel',
        send: 'Send email',
      },
    },
  },
  es: {
    matching: {
      language: { label: 'Idioma', english: 'English', spanish: 'Español' },
      animal: { cat: 'gato', dog: 'perro' },
      nav: {
        defaultLogoAlt: 'Logo de {{brand}}',
        shelterLogoAlt: 'Logo del refugio',
        shelterWebsite: 'Sitio web de {{name}}',
        visitShelterWebsite: 'Visitar el sitio web del refugio',
        loadingShelterLogo: 'Cargando el logo del refugio',
      },
      species: {
        headingStart: 'Tu',
        headingHighlight: 'amigo para siempre',
        headingEnd: 'te espera',
        intro:
          'No solo encontramos mascotas: elegimos compañeros para toda la vida. Comienza tu aventura eligiendo a tu futuro compañero.',
        dogChoice: 'Encuéntrame un perro',
        catChoice: 'Encuéntrame un gato',
        dogAlt: 'Golden retriever',
        catAlt: 'Gato atigrado naranja',
        heroAlt:
          'Persona sonriendo mientras sostiene un cachorro al aire libre',
        matches: 'Más de 5,000 matches',
        matchesSubtitle: 'Encontraron a su compañero ideal este mes',
        shelterNamed:
          'Estás viendo las mascotas disponibles en {{name}}. Los resultados se clasifican solo dentro de ese refugio.',
        shelterOnly:
          'Los resultados se clasifican solo entre las mascotas disponibles de este refugio (no en todo el sitio).',
      },
      quiz: {
        cancel: 'Cancelar',
        heading: 'Encontremos un {{species}}',
        shelterOnly:
          'Los resultados incluyen solo las mascotas disponibles de este refugio en nuestro sistema.',
        loading: 'Cargando preguntas…',
        loadError: 'No se pudieron cargar las preguntas',
        matchError: 'No se pudieron cargar los matches',
        retry: 'Reintentar',
        completeAll: 'Completa cada pregunta antes de buscar matches.',
        findMatches: 'Buscar matches →',
        nextStep: 'Siguiente paso →',
        back: 'Atrás',
        step: 'Paso {{current}} de {{total}}',
        progressAria: 'Paso {{current}} de {{total}}, {{percent}} por ciento',
        illustrationAlt: 'Un perro y un gato sentados juntos en un sofá',
        importance: {
          not_at_all: 'Nada',
          not_very: 'No mucho',
          very: 'Mucho',
        },
        emptyShelter:
          'Este refugio aún no tiene {{species}} disponibles con perfiles de compatibilidad completos en nuestro sistema. Prueba con la otra especie o vuelve más tarde.',
      },
      results: {
        back: 'Atrás',
        restart: 'Reiniciar test',
        heading: 'Tus matches de mascotas',
        subtitle:
          'Según tus preferencias, estas son algunas mascotas que creemos que te encantarán',
        share: 'Compartir resultados',
        adoption: 'Iniciar solicitud de adopción',
        shareHelp: 'Agrega matches antes de compartir.',
        noMatches: 'Aún no hay matches',
        empty:
          'No pudimos clasificar ningún registro de {{species}} en los datos del refugio. Regresa e inténtalo de nuevo o elige la otra especie.',
        emptyShared:
          'No se incluyeron mascotas en este conjunto de resultados compartidos.',
        matchBadge: '{{percent}}% Match',
        photo: 'Foto de {{name}}',
        petPhoto: 'Foto de mascota',
        noPhotoAvailable: 'No hay foto disponible',
        noPhoto: 'Sin foto',
        viewProfile: 'Ver perfil',
        viewProfileFor: 'Ver el perfil de {{name}}',
        favorite: 'Favorito',
        removeFavorite: 'Quitar a {{name}} de favoritos',
      },
      shared: {
        loading: 'Cargando resultados…',
        notFound: 'No se encontraron los resultados',
        unavailable: 'Resultados no disponibles',
        expired:
          'Es posible que el enlace haya vencido o que el quiz se realizara antes de habilitar la opción de compartir.',
        takeQuiz: 'Hacer el quiz de adopción',
        home: 'Inicio',
        note: 'Resultados compartidos de {{species}} del {{date}}. La clasificación refleja la disponibilidad al completar el quiz.',
      },
      share: {
        title: 'Mis resultados de mascotas',
        message: 'Mira mis resultados personales de compatibilidad',
        topOne: 'Tu mejor match',
        topThree: 'Tus 3 mejores matches',
        all: 'Todos los matches',
        close: 'Cerrar vista para compartir',
        slides: 'Diapositivas para compartir',
        previous: 'Anterior',
        previousSlide: 'Diapositiva anterior',
        next: 'Siguiente',
        nextSlide: 'Diapositiva siguiente',
        slideOf: 'Diapositiva {{current}} de {{total}}',
        dialogDescription:
          'Comparte los resultados de tu quiz. Vista actual: {{title}}. Diapositiva {{current}} de {{total}}. {{instructions}} Presiona Escape para cerrar. Enlace público: {{url}}',
        autoAdvance:
          'Las diapositivas avanzan automáticamente cada {{seconds}} segundos.',
        indicators: 'Usa los indicadores para cambiar de diapositiva.',
        buttons:
          'Usa los botones anterior y siguiente para cambiar de diapositiva.',
        socials: 'Compartir en redes',
        via: 'Compartir por {{channel}}',
        disabled:
          'Ejecuta Buscar matches con la opción de compartir habilitada para generar un enlace público',
        emailDisabled:
          'Ejecuta Buscar matches con la opción de compartir habilitada para enviar los resultados por email',
        topMatch: 'Mejor match',
        rank: 'Posición {{rank}}',
        statusShared:
          'Elige {{platform}} en el menú para publicar tus resultados o pégalos desde el portapapeles.',
        statusFacebookCopied:
          'Tu mensaje y enlace se copiaron. Abre Facebook y pégalos en una publicación, historia o mensaje.',
        statusCopied:
          '{{platform}} no permite compartir con un clic desde la web. Tu mensaje y enlace se copiaron. Abre la app de {{platform}} y pégalos en una historia, mensaje o publicación.',
        statusCopyFailed:
          'No se pudo copiar automáticamente. Comparte este enlace: {{url}}',
        statusNoLink:
          'No se pudo copiar automáticamente. Vuelve a buscar matches para generar un enlace.',
      },
      email: {
        unavailable:
          'El enlace para compartir no está disponible. Vuelve a buscar matches.',
        invalid: 'Ingresa una dirección de email válida.',
        disabled:
          'El envío de emails está deshabilitado en este entorno. Inténtalo más tarde.',
        failed: 'No se pudo enviar el email. Inténtalo de nuevo.',
        close: 'Cerrar diálogo de email',
        heading: 'Envía tus resultados por email',
        intro:
          'Enviaremos un resumen con tus matches clasificados a la dirección indicada.',
        email: 'Email',
        name: 'Nombre',
        optional: '(opcional)',
        consent: 'Al agregar tu email, aceptas compartir tus datos',
        missingId:
          'Falta el identificador para compartir. Completa un nuevo match para habilitar el email.',
        cancel: 'Cancelar',
        send: 'Enviar email',
      },
    },
  },
} as const;
